import { useEffect, useMemo } from "react";
import { z } from "zod";
import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  DashboardInviteStatus,
  MembersResponseSchema,
  PublicUser,
  type UserStatusReportSelection,
} from "@forgepoint/shared";

export type UserRole = "ADMIN" | "USER";
export type UserStatus = "ACTIVE" | "DISABLED";

export interface User {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  phone: string | null;
  role: UserRole;
  roleId: string | null;
  dashboardInviteStatus: DashboardInviteStatus;
  statusReportTemplate: UserStatusReportSelection;
  organization: {
    id: string;
    name: string;
  };
  status: UserStatus;
}

export interface CreateUserData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: UserRole;
  roleId: string;
  dashboardInviteStatus?: DashboardInviteStatus;
  statusReportTemplate?: UserStatusReportSelection;
}

export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  role?: UserRole;
  roleId?: string;
  dashboardInviteStatus?: DashboardInviteStatus;
  statusReportTemplate?: UserStatusReportSelection;
}

interface TeamsStore {
  users: User[];
  /** Map of userId -> user object, populated from full fetches and partial hydrations. */
  userMap: Map<string, PublicUser>;
  loading: boolean;
  error: string | null;
  membersRequested: boolean;

  fetchUsers: () => Promise<void>;
  /** Fetches active org members into the user map once per session. */
  ensureMembers: () => Promise<void>;
  createUser: (userData: CreateUserData) => Promise<User | null>;
  updateUser: (
    userId: string,
    userData: UpdateUserData,
  ) => Promise<User | null>;
  updateUserStatus: (userId: string, status: UserStatus) => Promise<boolean>;
  /** Merge partial user data into the user map without fetching all users. */
  mergeUsers: (users: PublicUser[]) => void;
  /** Look up a user by ID from the map. */
  getUserById: (userId: string) => PublicUser | undefined;
}

export const useTeamsStore = create<TeamsStore>((set, get) => ({
  users: [],
  userMap: new Map(),
  loading: false,
  error: null,
  membersRequested: false,

  ensureMembers: async () => {
    if (get().membersRequested) {
      return;
    }
    set({ membersRequested: true });
    try {
      const response = await fetchApi("users/members", "GET");
      if (!response.ok) {
        return;
      }
      const parsed = MembersResponseSchema.parse(response.data);
      get().mergeUsers(parsed.users);
    } catch (error) {
      captureException(error as Error);
    }
  },

  fetchUsers: async () => {
    set({ loading: true, error: null });

    try {
      const response = await fetchApi("users", "GET");

      if (response.ok && response.data) {
        const data = response.data as { users: User[] };
        get().mergeUsers(data.users);
        set({ users: data.users, loading: false });
        return;
      }

      set({ error: "Failed to fetch users", loading: false });
    } catch (error) {
      captureException(error as Error);
      set({ error: "Failed to fetch users", loading: false });
    }
  },

  createUser: async (userData: CreateUserData) => {
    set({ loading: true, error: null });

    try {
      const response = await fetchApi("users", "POST", {
        body: userData,
      });

      if (response.ok && response.data) {
        const data = response.data as { user: User };
        const newUser = data.user;
        set((state) => ({
          users: [...state.users, newUser],
          loading: false,
        }));
        return newUser;
      }

      set({ error: "Failed to create user", loading: false });
      return null;
    } catch (error) {
      captureException(error as Error);
      set({ error: "Failed to create user", loading: false });
      return null;
    }
  },

  updateUser: async (userId: string, userData: UpdateUserData) => {
    set({ loading: true, error: null });

    try {
      const response = await fetchApi(`users/${userId}`, "PUT", {
        body: userData,
      });

      if (response.ok && response.data) {
        const data = response.data as { user: User };
        const updatedUser = data.user;
        set((state) => ({
          users: state.users.map((user) =>
            user.id === userId ? updatedUser : user,
          ),
          loading: false,
        }));
        return updatedUser;
      }

      set({
        error: serverErrorMessage(response.data, "Failed to update user"),
        loading: false,
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      set({ error: "Failed to update user", loading: false });
      return null;
    }
  },

  updateUserStatus: async (userId: string, status: UserStatus) => {
    set({ loading: true, error: null });

    try {
      const response = await fetchApi(`users/${userId}/status`, "PATCH", {
        body: { status },
      });

      if (response.ok) {
        // Refresh the users list to get the updated status
        await get().fetchUsers();
        return true;
      }

      set({ error: `Failed to update user status`, loading: false });
      return false;
    } catch (error) {
      captureException(error as Error);
      set({ error: `Failed to update user status`, loading: false });
      return false;
    }
  },

  mergeUsers: (users: PublicUser[]) => {
    if (users.length === 0) {
      return;
    }
    set((state) => {
      const newMap = new Map(state.userMap);
      users.forEach((u) => {
        newMap.set(u.id, { ...newMap.get(u.id), ...u });
      });
      return { userMap: newMap };
    });
  },

  getUserById: (userId: string) => {
    return get().userMap.get(userId);
  },
}));

/**
 * Extracts the server-provided error message from an API response body,
 * falling back to a generic message when none is present.
 */
function serverErrorMessage(data: unknown, fallback: string): string {
  const parsed = z.object({ error: z.string() }).safeParse(data);
  return parsed.success ? parsed.data.error : fallback;
}

/**
 * Returns a display name for a user (first + last name, or email as fallback).
 * @param user - The user object (full or partial)
 * @returns The display name string
 */
export function getUserDisplayName(user: PublicUser): string {
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : user.email;
}

/**
 * Returns up to two uppercase initials for a user (first + last name, or the
 * first character of the email as fallback).
 * @param user - The user object (full or partial)
 * @returns The initials string
 */
export function getUserInitials(user: PublicUser): string {
  const first = user.firstName?.charAt(0) ?? user.email.charAt(0);
  const last = user.lastName?.charAt(0) ?? "";
  return (first + last).toUpperCase();
}

/**
 * Hook that returns a memoized Map of user ID to display name.
 * Automatically fetches users if they haven't been loaded yet.
 * @returns Map of user IDs to their display names
 */
export function useUserDisplayNames(): Map<string, string> {
  const users = useTeamsStore((state) => state.users);
  const loading = useTeamsStore((state) => state.loading);
  const fetchUsers = useTeamsStore((state) => state.fetchUsers);

  useEffect(() => {
    if (users.length === 0 && !loading) {
      fetchUsers();
    }
  }, [users.length, loading, fetchUsers]);

  return useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((user) => {
      map.set(user.id, getUserDisplayName(user));
    });
    return map;
  }, [users]);
}

/**
 * Hook that returns the id-keyed map of org members (public fields incl.
 * profile picture URL). Fetches the member list once per session; available
 * to any authenticated user.
 */
export function useOrgMembers(): Map<string, PublicUser> {
  const userMap = useTeamsStore((state) => state.userMap);
  const ensureMembers = useTeamsStore((state) => state.ensureMembers);

  useEffect(() => {
    ensureMembers();
  }, [ensureMembers]);

  return userMap;
}
